import { useMemo, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import { machineLabel } from '../data/planUi.ts'
import { costMachines, PICK_MACHINES } from '../data/finance.ts'
import { BackLink, Field, NotAllowed, Pill } from '../ui/components.tsx'
import { AssetCosts } from './AssetCosts.tsx'

/** Réglages › Coûts (M15-E1, docs/M15.md E1): a machine's costs for the direction (finance.read): pick a machine of the register (the device's copy of the
 *  fleet, searched by tag or model; `?asset=` names one), then its report, its invoices and its financials, read online behind the second factor and kept
 *  in the page only (AssetCosts). The same section opens on the machine's own page (the "Coûts" fold). */
export function CostsPage() {
  const { t } = useTranslation()
  const { can, kiosk } = useClub()
  const search = useSearch({ strict: false }) as { asset?: string }
  const assets = useAssets()
  const [picked, setPicked] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const machines = useMemo(() => costMachines(assets ?? []), [assets])
  const back = <BackLink to="/settings">← {t('settings.title')}</BackLink>
  if (kiosk || !can('finance.read')) return <section className="flex flex-col gap-4" data-testid="costs-page">{back}<h1 className="text-2xl font-bold">{t('costs.title')}</h1><NotAllowed permission="finance.read" /></section>
  if (!assets) return <p>{t('common.loading')}</p>
  const wanted = picked ?? search.asset ?? null
  const asset = wanted ? assets.find((a) => a.id === wanted) : undefined
  const q = query.trim().toUpperCase()
  const matches = q ? machines.filter((a) => a.readable_tag.toUpperCase().includes(q) || machineLabel(a).toUpperCase().includes(q) || (a.make ?? '').toUpperCase().includes(q)) : machines
  const shown = matches.slice(0, PICK_MACHINES)
  return (
    <section className="flex flex-col gap-4" data-testid="costs-page">
      {back}
      <div>
        <h1 className="text-2xl font-bold">{t('costs.title')}</h1>
        <p className="meta">{t('costs.pageHelp')}</p>
      </div>
      {asset ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-lg font-semibold" data-testid="costs-machine">{machineLabel(asset)}{asset.lifecycle === 'retired' && <Pill tone="gray">{t('assets.retiredPill')}</Pill>}</p>
            <button type="button" className="btn" onClick={() => { setPicked(null); setQuery('') }} data-testid="costs-change">{t('costs.changeMachine')}</button>
          </div>
          <AssetCosts key={asset.id} asset={asset} />
        </>
      ) : (
        <div className="flex flex-col gap-3" data-testid="costs-pick">
          <Field label={t('costs.searchMachine')} htmlFor="costs-search">
            <input id="costs-search" type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="costs-search" />
          </Field>
          {q && matches.length === 0 && <p className="meta" data-testid="costs-no-machine">{t('costs.noMachine')}</p>}
          <ul className="flex flex-col gap-2">
            {shown.map((a) => (
              <li key={a.id}>
                <button type="button" className="btn w-full justify-start" onClick={() => setPicked(a.id)} data-testid="costs-pick-machine" data-asset={a.id}>
                  <span className="min-w-0 truncate">{machineLabel(a)}</span>{a.lifecycle === 'retired' && <Pill tone="gray">{t('assets.retiredPill')}</Pill>}
                </button>
              </li>
            ))}
          </ul>
          {matches.length > shown.length && <p className="meta" data-testid="costs-more">{t('costs.moreMachines', { count: matches.length - shown.length })}</p>}
        </div>
      )}
    </section>
  )
}
